import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { customers as api, customerStatementPdfUrl, emailStatement, apiError } from "@/lib/api";
import { money, fmtDate } from "@/lib/format";
import { PageHeader, Card, Loading } from "@/components/Shared";
import { StatusBadge } from "@/components/StatusBadge";
import { ShareButton } from "@/components/ShareButton";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, Mail, Phone, MapPin, Receipt, FileText, FileCheck, FileDown } from "lucide-react";

export default function CustomerDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [c, setC] = useState(null);

  useEffect(() => { api.get(id).then(setC).catch((e) => toast.error(apiError(e))); }, [id]);
  const [emailing, setEmailing] = useState(false);
  const [dfrom, setDfrom] = useState("");
  const [dto, setDto] = useState("");
  const sendStatement = async () => {
    setEmailing(true);
    try {
      const r = await emailStatement(id, { date_from: dfrom || undefined, date_to: dto || undefined });
      toast.success(r.status === "sent" ? "Statement emailed" : "Statement queued (email not configured)");
    }
    catch (e) { toast.error(apiError(e)); }
    finally { setEmailing(false); }
  };
  if (!c) return <Loading />;

  const outstanding = (c.invoices || []).filter((i) => i.status !== "draft").reduce((a, i) => a + Math.max(i.total - (i.amount_paid || 0), 0), 0);

  return (
    <div>
      <button onClick={() => nav("/customers")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#0B132B] mb-4" data-testid="customer-detail-back"><ArrowLeft className="w-4 h-4" /> Back to customers</button>
      <PageHeader title={c.name} subtitle={c.vat_number ? `VAT ${c.vat_number}` : "Customer profile"}>
        <div className="flex items-center gap-1.5" data-testid="statement-date-range">
          <input type="date" value={dfrom} onChange={(e) => setDfrom(e.target.value)} className="h-9 rounded-lg border border-slate-200 px-2 text-sm text-slate-600" aria-label="Statement from date" data-testid="statement-date-from" />
          <span className="text-slate-400 text-sm">–</span>
          <input type="date" value={dto} onChange={(e) => setDto(e.target.value)} className="h-9 rounded-lg border border-slate-200 px-2 text-sm text-slate-600" aria-label="Statement to date" data-testid="statement-date-to" />
        </div>
        <a href={customerStatementPdfUrl(id, dfrom, dto)} download data-testid="download-statement"><Button variant="outline"><FileDown className="w-4 h-4 mr-1.5" /> Statement PDF</Button></a>
        <ShareButton docType="statement" docId={id} label="Share Statement" />
        <Button variant="outline" onClick={sendStatement} disabled={emailing} data-testid="email-statement"><Mail className="w-4 h-4 mr-1.5" /> {emailing ? "Sending…" : "Email Statement"}</Button>
        <Button onClick={() => nav("/invoices/new")} className="bg-[#0B132B] hover:bg-[#1C2541]" data-testid="customer-new-invoice"><FileText className="w-4 h-4 mr-1.5" /> New Invoice</Button>
      </PageHeader>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="p-5">
          <h3 className="font-display font-semibold text-[#0B132B] mb-3">Contact</h3>
          <div className="space-y-2.5 text-sm">
            <div className="flex items-center gap-2.5 text-slate-600"><Mail className="w-4 h-4 text-slate-400" /> {c.email || "—"}</div>
            <div className="flex items-center gap-2.5 text-slate-600"><Phone className="w-4 h-4 text-slate-400" /> {c.phone || "—"}</div>
            <div className="flex items-start gap-2.5 text-slate-600"><MapPin className="w-4 h-4 text-slate-400 mt-0.5" /> {c.address || "—"}</div>
          </div>
          <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-2 gap-3">
            <div><div className="text-xs text-slate-500">Invoices</div><div className="font-display text-xl font-bold text-[#0B132B]">{c.invoices?.length || 0}</div></div>
            <div><div className="text-xs text-slate-500">Outstanding</div><div className="font-display text-xl font-bold text-[#0B132B]">{money(outstanding)}</div></div>
          </div>
        </Card>

        <Card className="lg:col-span-2 overflow-hidden">
          <div className="p-5 border-b border-slate-100 flex items-center gap-2"><Receipt className="w-4 h-4 text-[#D4AF37]" /><h3 className="font-display font-semibold text-[#0B132B]">Invoice History</h3></div>
          {c.invoices?.length ? (
            <div className="divide-y divide-slate-100">
              {c.invoices.map((i) => (
                <button key={i.id} onClick={() => nav(`/invoices/${i.id}`)} className="w-full flex items-center justify-between px-5 py-3 hover:bg-slate-50 text-left" data-testid={`customer-invoice-${i.id}`}>
                  <div><div className="font-semibold text-[#0B132B] text-sm">{i.number}</div><div className="text-xs text-slate-400">{fmtDate(i.issue_date)}</div></div>
                  <div className="flex items-center gap-3"><span className="text-sm font-semibold text-[#0B132B]">{money(i.total)}</span><StatusBadge status={i.display_status} /></div>
                </button>
              ))}
            </div>
          ) : <div className="p-8 text-center text-sm text-slate-400">No invoices yet</div>}

          <div className="p-5 border-t border-b border-slate-100 flex items-center gap-2"><FileCheck className="w-4 h-4 text-[#D4AF37]" /><h3 className="font-display font-semibold text-[#0B132B]">Quote History</h3></div>
          {c.quotes?.length ? (
            <div className="divide-y divide-slate-100">
              {c.quotes.map((q) => (
                <button key={q.id} onClick={() => nav(`/quotes/${q.id}`)} className="w-full flex items-center justify-between px-5 py-3 hover:bg-slate-50 text-left" data-testid={`customer-quote-${q.id}`}>
                  <div><div className="font-semibold text-[#0B132B] text-sm">{q.number}</div><div className="text-xs text-slate-400">{fmtDate(q.issue_date)}</div></div>
                  <div className="flex items-center gap-3"><span className="text-sm font-semibold text-[#0B132B]">{money(q.total)}</span><StatusBadge status={q.status} /></div>
                </button>
              ))}
            </div>
          ) : <div className="p-8 text-center text-sm text-slate-400">No quotes yet</div>}
        </Card>
      </div>
    </div>
  );
}
